import Alert from '@cobliteam/ui/components/Alert';
import type { Workbook, Worksheet } from 'exceljs';
import saveAs from 'file-saver';
import moment from 'moment';

const LIGHT_GRAY_CODE = 'F5F5F5';
const FONT_COLOR = '07074e';

const TABLE_FIRST_CONTENT_ROW = 7;

interface IAddColumnTitles {
  sheet: Worksheet;
  columns: string[];
}

interface IAddHeader {
  sheet: Worksheet;
  numColumns: number;
  title: string;
}

interface IConfigSheetColumns {
  sheet: Worksheet;
  numColumns: number;
  columnWidth?: number;
}

interface IAddRows {
  sheet: Worksheet;
  rows: any;
  columns: string[];
}

interface IBuildSummarySheet {
  sheet: Worksheet;
  title: string;
  rows: any[];
  columns: string[];
}

interface IGetWorkbookProps {
  title: string;
  rows: any[];
  columns: string[];
}

export type TExportType = 'xlsx' | 'csv' | 'json';

export interface IBuildExcelProps {
  data: any;
  title: string;
  fileName: string;
  rows: any[];
  columns: string[];
  copy?: boolean;
  exportType: TExportType;
}

const setBorder = (sheet: Worksheet, cellName: string) => {
  sheet.getCell(cellName).border = {
    top: { style: 'thin' },
    left: { style: 'thin' },
    bottom: { style: 'thin' },
    right: { style: 'thin' },
  };
};

const fillRow = (sheet: Worksheet, row: number, color: string) => {
  sheet.getRow(row).eachCell({ includeEmpty: true }, (cell) => {
    sheet.getCell(cell.address).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: color },
    };
  });
};

const fontFormat = (size = 10) => ({
  size,
  bold: true,
  name: 'Calibri',
  color: { argb: FONT_COLOR },
});

const addHeader = ({ sheet, numColumns, title }: IAddHeader) => {
  const lastColumnLabel = String.fromCharCode('A'.charCodeAt(0) + numColumns - 1);

  sheet.mergeCells(`A1:${lastColumnLabel}1`);
  sheet.getCell('A1').value = `EXPORTADO EM ${moment().format('DD/MM/YYYY')} - ${moment().format('HH:mm')}`;
  sheet.getCell('A1').alignment = { horizontal: 'left' };
  setBorder(sheet, 'A1');

  fillRow(sheet, 1, LIGHT_GRAY_CODE);
  sheet.getRow(1).font = fontFormat();
  sheet.mergeCells(`A3:${lastColumnLabel}4`);

  sheet.getCell('A3').value = title;
  sheet.getCell('A3').alignment = { horizontal: 'center', vertical: 'middle' };
  setBorder(sheet, 'A3');

  fillRow(sheet, 3, LIGHT_GRAY_CODE);
  sheet.getRow(3).font = fontFormat(12);
  sheet.mergeCells(`A5:${lastColumnLabel}5`);
};

const addColumnTitles = ({ sheet, columns }: IAddColumnTitles) => {
  columns.forEach((columnName, index) => {
    const shouldFillWithLightGray = index % 2 === 0;
    const columnCode = `${String.fromCharCode('A'.charCodeAt(0) + index)}6`;

    sheet.getCell(columnCode).value = columnName;
    sheet.getCell(columnCode).font = fontFormat();
    sheet.getCell(columnCode).alignment = {
      horizontal: 'center',
      vertical: 'middle',
      wrapText: true,
    };
    setBorder(sheet, columnCode);
    if (shouldFillWithLightGray) {
      sheet.getCell(columnCode).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: LIGHT_GRAY_CODE },
      };
    }
  });
};

const configSheetColumns = ({ sheet, numColumns, columnWidth = 20 }: IConfigSheetColumns) => {
  sheet.columns = [...new Array(numColumns)].map((_, i) => ({
    width: i > 5 ? 35 : columnWidth,
    style: {
      font: {
        size: 10,
        name: 'Calibri',
        color: { argb: FONT_COLOR },
      },
      alignment: {
        vertical: 'middle',
        horizontal: 'center',
      },
    },
  }));
};

const addRows = ({ sheet, rows, columns }: IAddRows) => {
  sheet.addRows(rows);
  [...Array(rows.length).keys()].forEach((i) => {
    const rowNumber = i + TABLE_FIRST_CONTENT_ROW;
    [...Array(columns.length).keys()].forEach((j) => {
      const colNumber = j + 1;
      sheet.getCell(rowNumber, colNumber).border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
    });
  });
};

const buildSummarySheet = ({ sheet, rows, title, columns }: IBuildSummarySheet) => {
  const numColumns = columns.length;
  configSheetColumns({ sheet, numColumns });
  addHeader({ sheet, numColumns, title });
  addColumnTitles({ sheet, columns });
  addRows({ sheet, rows, columns });
};

const getWorkbook = async ({ title, rows, columns }: IGetWorkbookProps): Promise<Workbook> => {
  try {
    const ExcelJS = await import('exceljs');
    const workbook = new ExcelJS.Workbook();

    workbook.creator = 'Cobli';
    workbook.created = moment().toDate();
    workbook.title = title;

    const sheet = workbook.addWorksheet(title);
    buildSummarySheet({ sheet, rows, title, columns });

    return workbook;
  } catch (e) {
    throw new Error('Erro ao gerar planilha');
  }
};

const buildExcel = async ({ title, fileName, data, rows, columns, copy, exportType }: IBuildExcelProps) => {
  try {
    const workbook = await getWorkbook({ title, rows, columns });

    const fileType = {
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
      csv: 'text/csv;charset=UTF-8',
      json: 'application/json',
    }[exportType];
    const buffer = {
      xlsx: await workbook.xlsx.writeBuffer(),
      csv: await workbook.csv.writeBuffer(),
      json: JSON.stringify(data),
    }[exportType];

    if (copy) {
      navigator.clipboard.writeText(buffer.toString());
      Alert.success('Dados copiados para a área de transferência.');
      return true;
    }

    saveAs(new Blob([buffer], { type: fileType }), fileName);
    Alert.success('Export realizado com sucesso.');

    return true;
  } catch (e) {
    Alert.error('Erro ao gerar export');
    throw new Error('Erro ao gerar planilha');
  }
};

export default buildExcel;
